import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useRef, useState } from 'react'
import invariant from 'tiny-invariant'

import { Button } from '@/components/ui/button'
import { faPaperclip } from '@/lib/fontAwesomePro/light'
import { truncateText } from '@/lib/helpers/helperFunctions'
import { cn } from '@/lib/utils'
import { SerializableFile } from '@/types/misc'

export interface FilePickerProps {
  defaultSelectedFile?: SerializableFile | null
  buttonLabel?: React.ReactNode | string
  buttonVariant?: React.ComponentProps<typeof Button>['variant']
  buttonSize?: React.ComponentProps<typeof Button>['size']
  filePickerClassName?: string
  removeFileClassName?: string
  removeButtonClassName?: string
  onChange?: (file: File | null) => void
  icon?: React.ReactNode
  maxSizeMb: number
  onFileSizeError?: (limitInMb: number) => void
  accept?: string
}

const SerializableFilePicker: React.FC<FilePickerProps> = ({
  defaultSelectedFile,
  buttonLabel,
  buttonVariant = 'outline-primary',
  buttonSize,
  filePickerClassName,
  removeFileClassName,
  removeButtonClassName,
  icon,
  onChange,
  maxSizeMb,
  onFileSizeError,
  accept,
}) => {
  const [fileDownloadLink, setFileDownloadLink] = useState<string | undefined>(
    defaultSelectedFile?.url,
  )
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(
    determineInitialSelectedFile,
  )

  function determineInitialSelectedFile(): File | null {
    return defaultSelectedFile
      ? new File([defaultSelectedFile.url], defaultSelectedFile.name)
      : null
  }

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files
    const file = files && files[0] ? files[0] : null

    const isFileSizeError =
      maxSizeMb &&
      onFileSizeError &&
      file?.size &&
      file.size > maxSizeMb * 1024 * 1024 // Convert MB to bytes

    if (isFileSizeError) {
      onFileSizeError(maxSizeMb)
      return
    }

    setSelectedFile(file)

    if (file) {
      setFileDownloadLink(window.URL.createObjectURL(file))
    }

    if (onChange) {
      onChange(file)
    }
  }

  function handleRemoveFile() {
    if (fileInputRef.current?.value) {
      fileInputRef.current.value = ''
    }

    setSelectedFile(null)

    if (fileDownloadLink) {
      window.URL.revokeObjectURL(fileDownloadLink)
    }

    if (onChange) {
      onChange(null)
    }
  }

  function renderAttachButton() {
    return (
      <Button
        variant={buttonVariant}
        size={buttonSize}
        className={cn('flex items-center justify-center', filePickerClassName)}
        onClick={() => {
          fileInputRef.current?.click()
        }}
      >
        {icon ? (
          icon
        ) : (
          <FontAwesomeIcon
            role="button"
            icon={faPaperclip}
            style={{ fontSize: '14px' }}
          />
        )}
        {buttonLabel}
      </Button>
    )
  }

  function renderFile() {
    invariant(selectedFile, 'Expected selectedFile to be defined')

    return (
      <div className={cn(removeFileClassName, 'flex items-center')}>
        <a
          href={fileDownloadLink}
          target="_blank"
          rel="noreferrer"
          className="text-primary"
        >
          {truncateText(selectedFile.name)}
        </a>

        <Button
          variant="plain"
          className={cn(removeButtonClassName, 'text-dim ml-2')}
          onClick={handleRemoveFile}
          style={{ fontSize: '11px' }}
        >
          (Remove)
        </Button>
      </div>
    )
  }

  return (
    <>
      {selectedFile ? renderFile() : renderAttachButton()}

      <input
        ref={fileInputRef}
        type="file"
        name="file"
        onChange={handleFileChange}
        className="hidden"
        accept={accept}
      />
    </>
  )
}

export default SerializableFilePicker
